<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-lang-picker="false"
  >
    <template #header_start>
      <ButtonBack @click="emit('back')" />
    </template>
    <template #header_middle>
      <div class="kds-typography-title-medium">
        {{ $t('b2b_self_test_generic_assessment') }}
      </div>
    </template>
    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <h3
          class="kds-typography-title-xlarge"
          v-html="$t('b2b_self_test_gender')"
        />
        <div
          class="kds-typography-paragraph-large"
          v-html="$t('b2b_self_test_about_description')"
        />
      </div>
      <div class="about-de">
        <div class="gender-groups">
          <button
            :aria-label="$t('b2b_self_test_gender_female')"
            :class="['item', 'female', { 'female--active': gender === 'f' }]"
            data-qa="gender-female"
            role="button"
            type="button"
            @click="setGender('f')"
            @touchstart.prevent="handleTouchStartEvent"
            v-html="$t('b2b_self_test_gender_female')"
          />
          <button
            :aria-label="$t('b2b_self_test_gender_male')"
            :class="['item', 'male', { 'male--active': gender === 'm' }]"
            data-qa="gender-male"
            role="button"
            type="button"
            @click="setGender('m')"
            @touchstart.prevent="handleTouchStartEvent"
            v-html="$t('b2b_self_test_gender_male')"
          />
          <button
            :aria-label="$t('b2b_self_test_gender_other')"
            :class="['item', 'other', { 'other--active': gender === 'u' }]"
            data-qa="gender-other"
            role="button"
            type="button"
            @click="setGender('u')"
            @touchstart.prevent="handleTouchStartEvent"
            v-html="$t('b2b_self_test_gender_other')"
          />
        </div>
      </div>
    </div>
    <template #actions>
      <BaseButton
        :disabled="!gender"
        :text="$t('generic_action_continue')"
        data-qa="submit"
        @click="next"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import BaseButton from '@shared/components/BaseButton.vue'
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import { computed } from 'vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'

const selfTestStore = useSelfTestStore()

const props = defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
})

const emit = defineEmits(['back'])

const gender = computed(() => selfTestStore.gender)

const setGender = (value) => {
  selfTestStore.gender = value
}

const isValid = computed(() => gender.value)

const next = () => {
  if (isValid.value) {
    props.handleNextEvent()
  }
}

const handleTouchStartEvent = (event) => {
  const handleTouchEndEvent = (event2) => {
    event2.target.removeEventListener('touchend', handleTouchEndEvent)
    event2.target.click()
  }
  event.target.addEventListener('touchend', handleTouchEndEvent)
}
</script>

<style lang="scss">
.about-de {
  .gender-groups {
    display: flex;
    justify-content: space-between;

    .item {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0;
      margin: 0;
      background-color: transparent;
      border: none;
      color: $kds-color-text-secondary;
      flex-basis: 30%;
      cursor: pointer;

      @include kds-typography-paragraph-large;

      &:before {
        content: '';
        display: inline-block;
        width: 100px;
        height: 100px;
        border-radius: 50%;
        margin-bottom: 16px;
        background: url('@shared/assets/img/gender-groups.svg') center no-repeat;
      }
    }

    .female {
      &:before {
        background-color: $kds-color-background-subtle;
        background-position: -88px 9px;
        background-size: 800%;
      }

      &--active:before {
        background-color: $kds-color-background-blue-accent;
        background-position: 9px 9px;
      }
    }

    .male {
      &:before {
        background-color: $kds-color-background-subtle;
        background-position: -380px 20px;
        background-size: 800%;
      }

      &--active:before {
        background-color: $kds-color-background-orange-accent;
        background-position: -240px 20px;
      }
    }

    .other {
      &:before {
        background-color: $kds-color-background-subtle;
        background-position: -679px 9px;
        background-size: 800%;
      }

      &--active:before {
        background-color: $kds-color-background-green-accent;
        background-position: -540px 9px;
      }
    }
  }
}
</style>
